import _ from 'lodash';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';

import AgentSelect from 'components/search/filters/agent_select';
import ChannelFilterContainer from 'components/search/filters/channel_filter_container';
import DateCreatedFilterContainer from 'components/search/filters/date_created_filter_container';
import DateUpdatedFilterContainer from 'components/search/filters/date_updated_filter_container';
import DropdownMenu from 'components/common/dropdown_menu';
import InboxSelect from 'components/search/filters/inbox_select';
import HitTypes from 'models/search/hit_types';
import SLAFilterContainer from 'components/search/filters/sla_filter_container';
import StatusFilterContainer from 'components/search/filters/status_filter_container';
import TopicSelect from 'components/search/filters/topic_select';
import i18n from 'scripts/i18n/i18n';

const CONVERSATION = HitTypes.CONVERSATION;
const CUSTOMER = HitTypes.CUSTOMER_PROFILE;
const TASK = HitTypes.TASK;

const TypesSelection = {
  ALL: 'all',
  CONVERSATIONS: 'conversations',
  TASKS: 'tasks',
};

const TYPES_BY_SELECTION = {
  [TypesSelection.ALL]: [CONVERSATION, TASK],
  [TypesSelection.CONVERSATIONS]: [CONVERSATION],
  [TypesSelection.TASKS]: [TASK],
};

const LABEL_BY_SELECTION = {
  [TypesSelection.ALL]: 'Conversations & Tasks',
  [TypesSelection.CONVERSATIONS]: 'Conversations',
  [TypesSelection.TASKS]: 'Tasks',
};

const TYPES_DROPDOWN_OPTIONS = [
  { label: LABEL_BY_SELECTION[TypesSelection.ALL], value: TypesSelection.ALL },
  { label: LABEL_BY_SELECTION[TypesSelection.CONVERSATIONS], value: TypesSelection.CONVERSATIONS },
  { label: LABEL_BY_SELECTION[TypesSelection.TASKS], value: TypesSelection.TASKS },
];

function getTypesSelection(type, types) {
  if (types && types.length) {
    const hasConv = types.includes(CONVERSATION);
    const hasTask = types.includes(TASK);
    if (hasConv && hasTask) {
      return TypesSelection.ALL;
    }
    if (hasTask) {
      return TypesSelection.TASKS;
    }
    return TypesSelection.CONVERSATIONS;
  }
  if (type === CONVERSATION) {
    return TypesSelection.CONVERSATIONS;
  }
  return TypesSelection.ALL;
}

function getConvTabCount(buckets, typesSelection) {
  const convCount = buckets[CONVERSATION] || 0;
  const taskCount = buckets[TASK] || 0;
  if (typesSelection === TypesSelection.TASKS) {
    return taskCount;
  }
  if (typesSelection === TypesSelection.CONVERSATIONS) {
    return convCount;
  }
  return convCount + taskCount;
}

class UniversalSearchFilter extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      cachedAggs: props.aggregations || null,
    };
    this.preventDefault = this.preventDefault.bind(this);
  }
  // So we don't lose focus when using the filters
  preventDefault(ev) {
    ev.preventDefault();
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.hasResult) {
      this.setState({
        cachedAggs: props.aggregations,
        cachedType: props.documentType,
        cachedTypes: props.documentTypes,
      });
    } else if (!props.hasResult && !props.queryId) {
      this.setState({ cachedAggs: null, cachedType: null, cachedTypes: null });
    }
  }

  render() {
    // aggregations is cache-first so the bucket counts persist visibly while a
    // re-query is in flight (props.aggregations is null mid-request). type and
    // types are props-first because they reflect the user's current selection —
    // the dropdown needs to react immediately to clicks, even before the next
    // result lands.
    const aggregations = this.state.cachedAggs || this.props.aggregations;
    const type = this.props.documentType || this.state.cachedType;
    const types = this.props.documentTypes || this.state.cachedTypes;

    const buckets = aggregations ? _.get(aggregations, 'types.buckets') || {} : {};
    const typesSelection = getTypesSelection(type, types);
    const convTabLabel = LABEL_BY_SELECTION[typesSelection];

    const convCount = aggregations ? getConvTabCount(buckets, typesSelection) : null;
    const custCount = aggregations ? buckets[CUSTOMER] || 0 : null;
    const isConvTabActive = type !== CUSTOMER;

    let conversationFilters = (
      <div className="universalSearch-conversationFilters">
        <div className="universalSearch-filter-facetSeparator" />
        <h5 className="universalSearch-filter-facetHeader">{i18n.t('Filter by:')}</h5>
        <div>
          <div className="universalSearch-filterHeader">{i18n.t('Type')}</div>
          {/* onMouseDown stops the native mousedown from reaching the document-level
              outside-click handler inside DropdownMenu's portalled popover, which would
              otherwise close the menu a moment before the opener's onClick reopens it. */}
          <div
            className="universalSearch-filter universalSearch-filter-types"
            onMouseDown={e => e.nativeEvent.stopPropagation()}
          >
            <DropdownMenu
              data-aid="universalSearch-filter-types"
              fitOpenerWidth
              onSelect={value => this.props.onSearchFilterChange({ types: TYPES_BY_SELECTION[value] })}
              options={TYPES_DROPDOWN_OPTIONS}
              value={typesSelection}
            />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Topic')}</div>
          <div className="universalSearch-filter universalSearch-filter-topic">
            <TopicSelect onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Status')}</div>
          <div className="universalSearch-filter universalSearch-filter-status">
            <StatusFilterContainer onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('SLA')}</div>
          <div className="universalSearch-filter universalSearch-filter-sla">
            <SLAFilterContainer onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Agent')}</div>
          <div className="universalSearch-filter universalSearch-filter-agents">
            <AgentSelect onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} selectedAgents={[]} />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Inboxes')}</div>
          <div className="universalSearch-filter universalSearch-filter-inbox">
            <InboxSelect onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Date')}</div>
          <div className="universalSearch-filter">
            <div className="universalSearch-filter-dateCreated">
              <DateCreatedFilterContainer onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
            </div>
            <div className="universalSearch-filter-dateUpdated">
              <DateUpdatedFilterContainer onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
            </div>
          </div>
          <div className="universalSearch-filterHeader">{i18n.t('Channels')}</div>
          <div className="universalSearch-filter universalSearch-filter-channel">
            <ChannelFilterContainer onChange={this.props.onSearchFilterChange} queryId={this.props.queryId} />
          </div>
        </div>
      </div>
    );

    return (
      <div className="universalSearch-filters-wrapper">
        <div className="universalSearch-filters">
          <h5 className="universalSearch-filter-facetHeader">{i18n.t('Search')}</h5>
          <ul className="universalSearch-filter-documentTypes">
            <li
              className={classnames(
                'universalSearch-filter-documentType',
                { 'universalSearch-filter-documentType-active': isConvTabActive },
                { 'universalSearch-filter-documentType-active-noResults': isConvTabActive && !convCount },
                { 'universalSearch-filter-documentType-noResults': !isConvTabActive && !convCount }
              )}
              data-aid="universalSearch-filter-conversation"
              onClick={() => this.props.onSearchFilterChange({ types: TYPES_BY_SELECTION[typesSelection] })}
              onMouseDown={this.preventDefault}
            >
              <div className="universalSearch-filter-documentType-label">{convTabLabel}</div>
              <div className="universalSearch-filter-documentType-count">{convCount && convCount.toLocaleString()}</div>
            </li>
            <li
              className={classnames(
                'universalSearch-filter-documentType',
                { 'universalSearch-filter-documentType-active': type === CUSTOMER },
                { 'universalSearch-filter-documentType-active-noResults': type === CUSTOMER && !custCount },
                { 'universalSearch-filter-documentType-noResults': type !== CUSTOMER && !custCount }
              )}
              data-aid="universalSearch-filter-customer"
              onClick={() => this.props.onSearchFilterChange({ type: CUSTOMER })}
              onMouseDown={this.preventDefault}
            >
              <div className="universalSearch-filter-documentType-label">{i18n.t('Customers')}</div>
              <div className="universalSearch-filter-documentType-count">{custCount && custCount.toLocaleString()}</div>
            </li>
          </ul>
          {type !== CUSTOMER && conversationFilters}
        </div>
      </div>
    );
  }
}

UniversalSearchFilter.propTypes = {
  aggregations: PropTypes.object,
  documentType: PropTypes.string,
  documentTypes: PropTypes.arrayOf(PropTypes.string),
  hasResult: PropTypes.bool.isRequired,
  onSearchFilterChange: PropTypes.func.isRequired,
  queryId: PropTypes.string,
};

export default UniversalSearchFilter;
